/**
 * Motion foundation (design system, FOUNDATION — DESIGN-BRIEF §v5 "PIXEL
 * SWEETHEART": bouncy, cute, delightful — squash-and-stretch sells the fun).
 *
 * One vocabulary of springs + variants for the whole app. Verticals import
 * these presets instead of inventing curves; `<MotionConfig reducedMotion="user">`
 * is mounted once in app.tsx, so framer transform/layout animations collapse
 * automatically for prefers-reduced-motion users. For behavior that must
 * change shape under reduced motion (announcer → toast, takeovers → instant),
 * use `usePrefersReducedMotion()` / `prefersReducedMotion()`.
 *
 * v5 notes:
 *  - every legacy export survives IN NAME (v2/v3/v4 compat), retuned with a
 *    little more bounce — springy overshoot, never violence;
 *  - NEW: springs.squish (button press with juice), springs.jelly (wobbly
 *    entrances — mascot pops, celebration bits), bounceIn (squash-stretch
 *    entrance), wiggle (a happy little shimmy for chips/badges/mascot).
 *  - shakeScreen() remains a gentle flinch and remains out of everyday flows.
 */
import { useSyncExternalStore } from "react";
import type { Transition, Variants } from "framer-motion";

/* ------------------------------------------------------------- springs ---- */
export const springs = {
  /** Button presses, small chips — tight and immediate. */
  press: { type: "spring", stiffness: 650, damping: 30, mass: 0.8 } as Transition,
  /** Default UI movement: sheets, cards, tab indicator. */
  snappy: { type: "spring", stiffness: 420, damping: 30 } as Transition,
  /** Entrances that should feel alive (announcer, reveals, chips). */
  bouncy: { type: "spring", stiffness: 480, damping: 20 } as Transition,
  /** Bigger surfaces: page transitions, deck card return. */
  gentle: { type: "spring", stiffness: 260, damping: 26 } as Transition,
  /** Slow, warm (match reveal card, full-screen takeovers). */
  dramatic: { type: "spring", stiffness: 180, damping: 20 } as Transition,
  /** The (former) slam: a lively pop with a whisper of overshoot.
   *  Announcer entrances, verdict tags. */
  slam: { type: "spring", stiffness: 520, damping: 26, mass: 0.9 } as Transition,
  /** Heavy mass: things that feel like they WEIGH something (the deck
   *  card returning to center, avatars settling together). */
  weighty: { type: "spring", stiffness: 210, damping: 20, mass: 1.3 } as Transition,
  /** v5 — the squish: press feedback with real juice (pair with a
   *  whileTap scale/squash). */
  squish: { type: "spring", stiffness: 700, damping: 22, mass: 0.6 } as Transition,
  /** v5 — jelly: visible wobble on arrival. Mascot pops, celebration
   *  sprites, anything that should feel ALIVE. */
  jelly: { type: "spring", stiffness: 380, damping: 13, mass: 0.9 } as Transition,
} as const;

/* ------------------------------------------------------------ variants ---- */
/** Route/page transition — a soft camera move: the outgoing screen recedes,
 *  the incoming one rises gently. Used by <RouteTransition/>. */
export const pageVariants: Variants = {
  initial: { opacity: 0, y: 14, scale: 0.99 },
  enter: { opacity: 1, y: 0, scale: 1, transition: springs.gentle },
  exit: { opacity: 0, y: -8, scale: 1.006, transition: { duration: 0.13 } },
};

/** Staggered list/grid entrance — parent gets `stagger`, children `rise`. */
export const stagger: Variants = {
  initial: {},
  enter: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
export const rise: Variants = {
  initial: { opacity: 0, y: 16 },
  enter: { opacity: 1, y: 0, transition: springs.snappy },
};

/** Pop-in for chips, badges, tags. */
export const pop: Variants = {
  initial: { opacity: 0, scale: 0.7 },
  enter: { opacity: 1, scale: 1, transition: springs.bouncy },
  exit: { opacity: 0, scale: 0.85, transition: { duration: 0.12 } },
};

/** v5 — squash-and-stretch entrance: lands wide, springs tall, settles.
 *  For mascot appearances, celebration sprites, playful cards. */
export const bounceIn: Variants = {
  initial: { opacity: 0, scaleX: 0.7, scaleY: 0.55, y: 10 },
  enter: {
    opacity: 1,
    scaleX: [0.7, 1.12, 0.96, 1],
    scaleY: [0.55, 0.86, 1.08, 1],
    y: [10, -4, 1, 0],
    transition: { duration: 0.5, times: [0, 0.45, 0.75, 1], ease: "easeOut" },
  },
  exit: { opacity: 0, scale: 0.85, transition: { duration: 0.14 } },
};

/** v5 — a happy little shimmy (±3°, one shot). Chips, badges, the mascot
 *  when something nice happens. Seizure-safe, honors reduced motion via
 *  MotionConfig. */
export const wiggle: Variants = {
  initial: { rotate: 0 },
  enter: {
    rotate: [0, -3, 3, -2, 1, 0],
    transition: { duration: 0.45, ease: "easeInOut" },
  },
};

/** v4 — soft press-in: settles from slightly above scale with a friendly
 *  bounce. Use for verdict tags, "ARMED" chips, reveal titles. */
export const stampVariants: Variants = {
  initial: { opacity: 0, scale: 1.35, rotate: -2 },
  enter: { opacity: 1, scale: 1, rotate: 0, transition: springs.slam },
  exit: { opacity: 0, scale: 0.92, transition: { duration: 0.12 } },
};

/** v4 — a tiny one-shot shiver (~0.3s, low amplitude, seizure-safe). Kept for
 *  the OPT-IN game layer; the everyday UI does not glitch anymore. */
export const glitchVariants: Variants = {
  initial: { opacity: 0, skewX: 0, x: 0 },
  enter: {
    opacity: [0, 1, 1, 1, 1],
    skewX: [3, -2, 1, 0, 0],
    x: [-3, 2, -1, 0, 0],
    transition: { duration: 0.3, times: [0, 0.25, 0.5, 0.75, 1] },
  },
};

/** Sheet: backdrop fade + panel spring-up (bottom sheet) / scale (dialog). */
export const sheetBackdropVariants: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1, transition: { duration: 0.18 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};
export const sheetPanelVariants: Variants = {
  initial: { opacity: 0, y: 48, scale: 0.98 },
  enter: { opacity: 1, y: 0, scale: 1, transition: springs.snappy },
  exit: { opacity: 0, y: 32, transition: { duration: 0.15 } },
};

/** Toast: slide in from below, settle, fade out. */
export const toastVariants: Variants = {
  initial: { opacity: 0, y: 16, scale: 0.97 },
  enter: { opacity: 1, y: 0, scale: 1, transition: springs.snappy },
  exit: { opacity: 0, y: 8, scale: 0.97, transition: { duration: 0.14 } },
};

/** Announcer: v4 — drops in warmly with a small bounce and settles. A
 *  celebratory nudge, not an arcade slam. */
export const announcerVariants: Variants = {
  initial: { opacity: 0, y: -28, scale: 1.04, x: "-50%" },
  enter: {
    opacity: 1,
    y: 0,
    scale: 1,
    x: "-50%",
    transition: springs.bouncy,
  },
  exit: { opacity: 0, y: -20, scale: 0.96, x: "-50%", transition: { duration: 0.18 } },
};

/* --------------------------------------------------------- screen shake --- */
let shakeTimer: number | undefined;

/**
 * v4 — a gentle whole-screen flinch (the #root subtree; portalled overlays
 * stay steady on purpose). CSS keyframes are ±2–4px now — a tremor, not a
 * slam. Transform-only; a no-op under prefers-reduced-motion. Exported for
 * compatibility; the everyday flows no longer call it.
 */
export function shakeScreen(intensity: "soft" | "hard" = "soft"): void {
  if (typeof document === "undefined" || prefersReducedMotion()) return;
  const root = document.getElementById("root");
  if (!root) return;
  const cls = intensity === "hard" ? "fx-shake-hard" : "fx-shake-soft";
  root.classList.remove("fx-shake-soft", "fx-shake-hard");
  // force a reflow so re-triggering restarts the animation
  void root.offsetWidth;
  root.classList.add(cls);
  window.clearTimeout(shakeTimer);
  shakeTimer = window.setTimeout(() => {
    root.classList.remove("fx-shake-soft", "fx-shake-hard");
  }, 500);
}

/* ------------------------------------------------------ reduced motion ---- */
const rmQuery = "(prefers-reduced-motion: reduce)";

/** Non-hook check (imperative code paths: announce(), swipe commits…). */
export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia(rmQuery).matches;
}

function subscribe(cb: () => void): () => void {
  const mql = window.matchMedia(rmQuery);
  mql.addEventListener("change", cb);
  return () => mql.removeEventListener("change", cb);
}

/** Reactive hook — big transitions go instant / announcer becomes a toast (PRD §10). */
export function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, prefersReducedMotion, () => false);
}
